import {
  AlarmClock,
  ArrowRight,
  CalendarClock,
  Clock,
  Gauge,
  MapPin,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { parseBackendDate } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { formatDateShort, type AutoletturaWindow } from '../types'

/**
 * Card della Home che invita all'invio dell'autolettura.
 * 3 modalità (vedi `AutoletturaWindow.mode`):
 *  - 'open'    → finestra aperta, CTA primaria (cyan o ambra se urgent)
 *  - 'preview' → la finestra apre tra 1 giorno → heads-up neutro senza CTA azione
 *  - 'closed'  → la card non viene renderizzata
 *
 * Finestra autolettura (convenzione Enermed):
 *  - ultimi 2 giorni del mese + primi 2 del successivo
 *  - 1 giorno prima dell'apertura → preview
 */

// Urgent = ultimo giorno della finestra di 4 (≤ 1 giorno alla chiusura).
const URGENCY_DAYS = 1

type Urgency = 'urgent' | 'normal'

function computeUrgency(closesOn?: string | null): Urgency {
  if (!closesOn) return 'normal'
  const closing = parseBackendDate(closesOn)
  if (!closing) return 'normal'
  const daysLeft = closing.startOf('day').diff(new Date(), 'day')
  return daysLeft >= 0 && daysLeft <= URGENCY_DAYS ? 'urgent' : 'normal'
}

export function AutoletturaCard({ window }: { window: AutoletturaWindow }) {
  if (window.mode === 'closed') return null
  if (window.mode === 'preview') return <PreviewCard window={window} />

  const isUrgent = computeUrgency(window.closesOn) === 'urgent'
  return <OpenCard window={window} urgent={isUrgent} />
}

// =====================================================================
// Variante OPEN (finestra attiva)
// =====================================================================

function OpenCard({
  window,
  urgent,
}: {
  window: AutoletturaWindow
  urgent: boolean
}) {
  return (
    <Card
      interactive
      className={cn(
        'relative overflow-hidden transition-colors',
        urgent
          ? 'border-warning/60 bg-gradient-to-br from-warning/15 via-warning/5 to-transparent shadow-md shadow-warning/20 dark:from-warning/30 dark:via-warning/10 dark:to-transparent'
          : 'border-secondary/30 bg-gradient-to-br from-secondary/10 via-secondary/5 to-transparent dark:from-secondary/20 dark:via-primary/30 dark:to-primary/50',
      )}
    >
      <Glow tone={urgent ? 'warning' : 'secondary'} />

      <CardContent className="relative flex flex-col gap-5 p-6 sm:flex-row sm:items-center">
        <IconBox tone={urgent ? 'warning' : 'secondary'}>
          {urgent ? <AlarmClock className="h-7 w-7" /> : <Gauge className="h-7 w-7" />}
        </IconBox>

        <div className="flex flex-1 flex-col gap-2">
          <HeaderRow window={window}>
            {urgent ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-warning/25 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-warning-foreground">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warning" aria-hidden />
                In scadenza
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-success">
                <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
                Finestra aperta
              </span>
            )}
          </HeaderRow>

          <p className="text-sm text-muted-foreground">
            {urgent
              ? 'Restano pochi giorni per inviare le letture del contatore (fasce F1, F2, F3).'
              : 'Inserisci le letture del contatore (fasce F1, F2, F3) per ottenere una fatturazione precisa.'}
          </p>

          {window.closesOn && (
            <div
              className={cn(
                'mt-1 flex items-center gap-1.5 text-xs font-semibold',
                urgent ? 'text-warning-foreground' : 'text-foreground',
              )}
            >
              <CalendarClock
                className={cn(
                  'h-3.5 w-3.5',
                  urgent ? 'text-warning-foreground' : 'text-secondary',
                )}
              />
              Entro il {formatDateShort(window.closesOn)}
            </div>
          )}
        </div>

        <Link
          to="/autolettura"
          className={cn(
            'inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold uppercase leading-none tracking-wide shadow-sm transition-all hover:shadow-md sm:self-center',
            urgent
              ? 'bg-warning text-warning-foreground shadow-warning/30 hover:bg-warning/90'
              : 'bg-secondary text-secondary-foreground shadow-secondary/30 hover:bg-secondary/90',
          )}
        >
          Invia ora
          <ArrowRight className="h-4 w-4" />
        </Link>
      </CardContent>
    </Card>
  )
}

// =====================================================================
// Variante PREVIEW (heads-up: apre domani)
// =====================================================================

function PreviewCard({ window }: { window: AutoletturaWindow }) {
  return (
    <Card className="relative overflow-hidden border-border bg-muted/30">
      <CardContent className="relative flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
        <IconBox tone="muted">
          <Clock className="h-6 w-6" />
        </IconBox>

        <div className="flex flex-1 flex-col gap-1">
          <HeaderRow window={window} preview>
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <Clock className="h-3 w-3" />
              In arrivo
            </span>
          </HeaderRow>
          <p className="text-sm text-muted-foreground">
            La finestra di invio dell’autolettura{' '}
            {window.opensOn ? (
              <>
                apre il{' '}
                <span className="font-semibold text-foreground">
                  {formatDateShort(window.opensOn)}
                </span>
              </>
            ) : (
              'aprirà a breve'
            )}
            {window.closesOn && (
              <>
                {' '}
                e si chiude il{' '}
                <span className="font-semibold text-foreground">
                  {formatDateShort(window.closesOn)}
                </span>
              </>
            )}
            . Tieniti pronto a leggere il contatore.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

// =====================================================================
// Helper riusabili tra le varianti
// =====================================================================

function Glow({ tone }: { tone: 'secondary' | 'warning' }) {
  const bg =
    tone === 'warning'
      ? 'radial-gradient(closest-side, hsl(var(--warning) / 0.7), hsl(var(--warning) / 0))'
      : 'radial-gradient(closest-side, hsl(var(--secondary) / 0.7), hsl(var(--secondary) / 0))'
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -right-20 -top-16 h-56 w-56 rounded-full opacity-30 blur-3xl dark:opacity-40"
      style={{ background: bg }}
    />
  )
}

function IconBox({
  tone,
  children,
}: {
  tone: 'secondary' | 'warning' | 'muted'
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl shadow-inner',
        tone === 'secondary' && 'bg-secondary/20 text-secondary shadow-secondary/20',
        tone === 'warning' &&
          'bg-warning/25 text-warning-foreground shadow-warning/25',
        tone === 'muted' && 'bg-muted text-muted-foreground shadow-muted/40',
      )}
    >
      {children}
    </div>
  )
}

function HeaderRow({
  window,
  preview,
  children,
}: {
  window: AutoletturaWindow
  preview?: boolean
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <h2
        className={cn(
          'font-bold text-foreground',
          preview ? 'text-base' : 'text-lg',
        )}
      >
        {preview ? 'Autolettura in arrivo' : 'Invia la tua autolettura'}
      </h2>
      {(window.podDisplay || window.podCode) && (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs">
          <MapPin className="h-3 w-3 shrink-0 text-secondary" />
          {window.podDisplay && (
            <span className="font-semibold text-foreground">{window.podDisplay}</span>
          )}
          {window.podCode && (
            <span
              className={
                window.podDisplay
                  ? 'font-mono text-[11px] text-muted-foreground'
                  : 'font-mono text-[11px] font-semibold text-foreground'
              }
            >
              {window.podCode}
            </span>
          )}
        </span>
      )}
      {children}
    </div>
  )
}
